import React from "react";
import type {
  Procedure,
  ProcedureDetail,
  ProcValidation,
  Region,
} from "../../../../application/domain/proc-management";
import { FormFieldLabel } from "../../../atoms/proc-management/form";
import {
  InputNoticeList,
  InputProps,
  ListInput,
  PathProps,
  ProcedureGroupNameInput,
  SimpleTextInput,
} from "../../../molecules/proc-management/form";
import { ServiceProviderR } from "../../../pages/service-provider/routes";

type Props = {
  procedure: ProcedureDetail | Procedure;
  onChange: (path: (string | number)[], value: any) => void;
  isServiceProviderAccount?: boolean;
  disabled?: boolean;
  shouldDisplayError: boolean;
  validationErrors: ProcValidation;
};

export const RelationsSection: React.FC<Props> = ({
  procedure,
  onChange,
  isServiceProviderAccount: isServiceProviderAccount = false,
  disabled = false,
  shouldDisplayError = false,
  validationErrors,
}) => {
  const { jurisdiction, regions } = procedure;

  return (
    <section className="mb-5">
      <h2 className="subtitle">管轄などに関する情報</h2>

      {
        // 自組織の情報しかみられないテナントユーザー側で表示する必要はないので
        // service provider account でのみ表示
        isServiceProviderAccount && (
          <div className="field mb-6">
            <FormFieldLabel label="管轄テナントID" />
            <div className="control">
              <input
                className="input"
                value={jurisdiction.organizationId}
                disabled
              />
            </div>
          </div>
        )
      }

      {
        // 職員側においてはmodalを用いて設定するので、
        // service provider account でのみ表示
        isServiceProviderAccount && (
          <div className="field mb-6">
            <FormFieldLabel label="管轄グループID" />
            <InputNoticeList
              notices={[
                <a
                  href={ServiceProviderR.Groups.link({
                    id: jurisdiction.organizationId,
                  })}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  グループ一覧
                </a>,
              ]}
            />
            <SimpleTextInput
              onChange={onChange}
              basePath={["jurisdiction", "groupId"]}
              value={jurisdiction.groupId}
              disabled // service provider accountには紐付け変更の権限がない
            />
          </div>
        )
      }

      <ProcedureGroupNameInput
        onChange={onChange}
        basePath={["jurisdiction", "groupName"]}
        value={jurisdiction.groupName}
        disabled={disabled}
        shouldDisplayError={shouldDisplayError}
        errorMsg={validationErrors.jurisdictionGrpName}
      />

      {
        // 現在は紐付けをロジック上利用していないので、
        // いったんservice provider accountのみで表示
        isServiceProviderAccount && (
          <ListInput
            label="手続きガイド地域情報"
            component={RegionInput}
            componentProps={regions.map((value) => ({
              ...value,
              onChange,
            }))}
            basePath={["regions"]}
            onPush={() => {
              onChange(["regions"], [...regions, {}]);
            }}
            onRemove={(i) => {
              onChange(
                ["regions"],
                [...regions.slice(0, i), ...regions.slice(i + 1)],
              );
            }}
            disabled={disabled}
          />
        )
      }
    </section>
  );
};

const RegionInput: React.FC<Region & InputProps & PathProps> = ({
  id,
  onChange,
  disabled = false,
  basePath,
}) => {
  return (
    <div>
      <FormFieldLabel label="id (id)" />
      <div className="content is-size-7 mb-2">
        <ul>
          <li>
            <a
              href="https://dashboard.graffer.jp/guides/regions#/"
              target="_blank"
              rel="noopener noreferrer"
            >
              「手続きガイド」の「地域」
            </a>
            と紐付けます
          </li>
        </ul>
      </div>
      <SimpleTextInput
        onChange={onChange}
        basePath={[...basePath, "id"]}
        value={id}
        disabled={disabled}
      />
    </div>
  );
};

export default RelationsSection;
